import {
    ControllerHelper,
    PostDownloadDto, SessionController, SessionDownloadDto,
    SessionTemplateDownloadDto
} from "collaboration-service";
import {PlaylistDetailNodeItemProps, useHidePlaylistNodeItem} from "../PlaylistDetailNodeItem";
import {ApplicationState, useAppSelector} from "../../../services/ApplicationState/ApplicationState";
import {getLink, shallowCompare} from "../../../services/Helpers";
import {useImgI18N} from "../../../services/ImgI18N";
import * as React from "react";
import { PlaylistDetailSC as T } from "../PlaylistDetailSC";
import {Dimmer, Icon, ImgIcons, useThemePart} from "imaginarity-react-ui";
import SafeHTML from "../../../components/SafeHTML/SafeHTML";
import {getTranslated} from "../../../services/Helpers/TranslationHelpers";
import {sanitizedNothingAllowedAttributes} from "../../../services/Helpers/SanitizedHelper";
import _ from "lodash";
import SessionsEnrollmentTable from "../../SessionManagement/SessionsEnrollmentTable";

const mapper = (s: ApplicationState) => ({
    currentGroup: s.currentGroup,
    contentLanguage: s.contentLanguage,
    filter: s.playlistsState.filter,
});

const SessionBasePlaylistNodeItem = (p: PlaylistDetailNodeItemProps<PostDownloadDto>) => {
    const { item, state, isOptional, nodeReference, transition, warning, isMobile, nodeIsDone} = p;

    const { contentLanguage } = useAppSelector(mapper, shallowCompare);
    const { t } = useImgI18N("sessionManagement");
    const [showModal, setShowModal] = React.useState<boolean>(false);
    const [sessions, setSessions] = React.useState<SessionDownloadDto[]>([]);
    const emptyImageUrl = useThemePart(t => t.emptyImageUrl);
    const sessionBase = item as unknown as SessionTemplateDownloadDto;
    
    React.useEffect(() => {
       const load = async () => {
           const s = await ControllerHelper.singleCall({groupId: sessionBase.group_id}, SessionController.GetSessions);
           const relevantSessions = _.filter(s, s => s.parent === sessionBase.id);
           setSessions(relevantSessions);
       };
       load();
    }, [sessionBase, setSessions]);
    
    
    const image = getLink(sessionBase.media, "self")?.uri ?? emptyImageUrl;
    const elementIsReference = item.id === nodeReference;

    // const clickOnItem = React.useMemo(() => () => {
    //     if (currentGroup && playlistId) {
    //         const ev: EventUploadDto = {
    //             eventType: "PostViewed",
    //             group_id: currentGroup.id,
    //             payload: {
    //                 "PostId": item.id,
    //                 "From": "playlist",
    //                 "PlaylistId": playlistId,
    //             }
    //         };
    //         ControllerHelper.singleCall({ ev }, EventController.PostEvent);
    //     }
    // }, [currentGroup, playlistId, item.id]);
    
    const toggleModal = React.useCallback(() => setShowModal(!showModal), [showModal]);
    
    const hidePlaylistNodeItem = useHidePlaylistNodeItem(p);
    
    return (
        <T.PLItemContainer isMobile={isMobile} isPreview={p.preview ?? false} style={{ background: hidePlaylistNodeItem.text === "open" ? "linear-gradient(90deg, rgba(49, 54, 57, 0.05) 10%, transparent 100%)" : undefined }}>
            <T.PLItemImage imageUrl={image} isMobile={isMobile} highlighted={elementIsReference} color={warning ? "@warning" : "@accentRed"} onClick={toggleModal} />
            <T.PLItemContainerContent isMobile={isMobile} onClick={toggleModal}>
                <T.PLItemContainerTitle isMobile={isMobile}>
                    {elementIsReference &&
                        <T.PLItemContainerTitleDot color={warning ? "@warning" : "@accentRed"} />
                    }
                    <SafeHTML html={getTranslated(sessionBase.headlines, contentLanguage)?.text ?? ""} allowedTags={[]} allowedAttributes={{}} />
                </T.PLItemContainerTitle>
                {!isMobile &&
                    <T.PLItemContainerDescription>
                        {sessionBase.descriptions &&
                            <SafeHTML
                                html={getTranslated(sessionBase.descriptions, contentLanguage).text ?? ""}
                                allowedTags={["strong", "span", "b", "i", "u"]}
                                allowedAttributes={sanitizedNothingAllowedAttributes}
                            />
                        }
                    </T.PLItemContainerDescription>
                }
                <T.PLItemContainerBottomCat isMobile={isMobile}>
                    <Icon name="wiki" marginTop={3} style={{ float: "left", marginLeft: -2, marginRight: 10 }} size="16px" />
                    {item.type}
                </T.PLItemContainerBottomCat>
            </T.PLItemContainerContent>
            {p.preview !== true &&
                <T.PLItemContainerRight isMobile={isMobile} onClick={toggleModal}>
                    {isOptional ?
                        <T.ColoredDiv color={state.state === "Finished" ? "@accentGreen" : "@lightGrey"}>
                            <div style={{ float: "right" }}>{state.state === "Finished" ? t("done") : t("optional")}</div>
                            <Icon name={state.state === "Finished" ? "check circle" : "empty"} style={{ float: "right", marginRight: 5 }} marginTop={4} color={state.state === "Finished" ? "@accentGreen" : "inherit"} />
                        </T.ColoredDiv>
                        :
                        (transition === "ReferenceDone" || transition === "ReferenceDoneWithValue") ?
                            elementIsReference ?
                                <T.ColoredDiv color={(hidePlaylistNodeItem.text === "100%" || nodeIsDone) ? "@accentGreen" : warning ? "@warning" : hidePlaylistNodeItem.color}>
                                    <div style={{ float: "right" }}>
                                        {(hidePlaylistNodeItem.text === "100%" || nodeIsDone) ? t("done") : warning ? t("warning") : hidePlaylistNodeItem.text}
                                    </div>
                                    <Icon name={(hidePlaylistNodeItem.text === "100%" || nodeIsDone) ? "check circle" : warning ? "exclamation triangle" : hidePlaylistNodeItem.icon as ImgIcons} style={{ float: "right", marginRight: 5 }} marginTop={4} color={(hidePlaylistNodeItem.text === "100%" || nodeIsDone) ? "@accentGreen" : warning ? "@warning" : hidePlaylistNodeItem.color} />
                                </T.ColoredDiv>
                                :
                                <T.ColoredDiv color={hidePlaylistNodeItem.text === "100%" ? "@accentGreen" : "@middleLightGrey"}>
                                    <div style={{ float: "right" }}>{hidePlaylistNodeItem.text === "100%" ? t("done") : t("optional")}</div>
                                    <Icon name="empty" style={{ float: "right", marginRight: 5 }} marginTop={4} />
                                </T.ColoredDiv>
                            :
                            <T.ColoredDiv color={hidePlaylistNodeItem.text === "100%" ? "@accentGreen" : warning ? "@warning" : hidePlaylistNodeItem.color}>
                                <div style={{ float: "right" }}>
                                    {hidePlaylistNodeItem.text === "100%" ? t("done") : t(hidePlaylistNodeItem.text)}
                                </div>
                                <Icon
                                    name={hidePlaylistNodeItem.icon as ImgIcons}
                                    style={{ float: "right", marginRight: 5 }}
                                    marginTop={4}
                                    color={hidePlaylistNodeItem.text === "100%" ? "@accentGreen" : warning ? "@warning" : hidePlaylistNodeItem.color}
                                />
                            </T.ColoredDiv>
                    }
                </T.PLItemContainerRight>
            }
            {showModal && <Dimmer active >
                <T.DimmerContainerWiki >
                    <T.StickyCloseButton>
                        <T.DimmerInfoContainerSticky onClick={toggleModal}>
                            <Icon name="times" size={27} marginTop={3} />
                        </T.DimmerInfoContainerSticky>
                    </T.StickyCloseButton>
                    <T.DimmerImageContainer imageUrl={_.find(sessionBase.media?.links, l => l.ref === "self")?.uri ?? emptyImageUrl} />
                    <T.DimmerInfoContainerWiki>
                        <T.DimmerInfoContainerTitle>
                            {getTranslated(sessionBase.headlines).text}
                        </T.DimmerInfoContainerTitle>    
                    </T.DimmerInfoContainerWiki>
                    
                    <T.DimmerInfoContainerDescription style={{ wordBreak: "break-word" }}>
                        <SafeHTML html={getTranslated(sessionBase.descriptions)?.text} />
                    </T.DimmerInfoContainerDescription>
                    <T.DimmerInfoContainerDescription>
                       <SessionsEnrollmentTable sessions={sessions} linkTarget="_blank" tooltip={{ position:"bottom-end", tooltipText: t("link opens in new browser tab"), zIndex: 2147483638 }} />
                    </T.DimmerInfoContainerDescription>
                </T.DimmerContainerWiki>
            </Dimmer>}
        </T.PLItemContainer>
    );
}

export default SessionBasePlaylistNodeItem;